// Avatar colors: the palette, and who in a workspace gets which tone.
//
// Pure (no React), so the Team directory normalizer (lib/teamDirectory.js) can
// validate a saved choice and the tests can check the assignment without
// rendering anything. components/Avatar.js draws the badge and reads the
// workspace assignment from a context fed by this module.
//
// HOW A PERSON GETS A COLOR
//  1. A tone chosen for them in the Team directory ("avatarColor" on the
//     teammate record, a palette key such as "sky"). Two people can choose the
//     same tone; that is their call.
//  2. Everyone else in the directory: their hash slot, unless somebody already
//     holds it, then the next slot nobody holds. Chosen tones are reserved
//     first, so "Automatic" never lands on a color someone picked. Processed
//     in directory order (the order teammates were added), so adding a
//     teammate never changes anyone else's color, and renaming someone never
//     changes anything. Past 12 people a tone has to repeat; the next slot is
//     then the least used one, so repeats spread evenly.
//  3. Anybody outside the directory: their hash slot.

// Only the top stop varies; the bottom of the gradient, the ring and the ink
// are theme tokens in components/Avatar.css. Contrast of the #14122a initials
// against the gradient where the letters sit is at least 5.3:1 at their middle
// and 4.5:1 at their baseline for every tone in dark mode, and 7.5:1 in light
// mode. Ordered so neighbours look nothing alike: when a slot is taken the
// next one is a clearly different color, not a near twin.
export const AVATAR_TONES = [
  { id: "gold", label: "Gold", top: "#ffe7a3" },
  { id: "sky", label: "Sky", top: "#8fd8f0" },
  { id: "rose", label: "Rose", top: "#f0b3d8" },
  { id: "mint", label: "Mint", top: "#a7f3d0" },
  { id: "periwinkle", label: "Periwinkle", top: "#9db4ff" },
  { id: "peach", label: "Peach", top: "#ffc4a3" },
  { id: "lime", label: "Lime", top: "#a3ee9b" },
  { id: "orchid", label: "Orchid", top: "#e7aef5" },
  { id: "aqua", label: "Aqua", top: "#86e3e3" },
  { id: "coral", label: "Coral", top: "#ffaaa5" },
  { id: "sage", label: "Sage", top: "#d6e8a4" },
  { id: "lavender", label: "Lavender", top: "#cdbdff" },
];

// The tone for a badge with nobody behind it (no name, no email).
export const DEFAULT_AVATAR_TONE = AVATAR_TONES[4];

const TONE_INDEX = new Map(AVATAR_TONES.map((tone, index) => [tone.id, index]));

function clean(value) {
  return String(value == null ? "" : value).trim();
}

// A saved choice, validated: a known palette key, or "" for automatic.
// Anything else (an old key, a hex code, a typo, a non-string) is automatic,
// so a stray value can never reach a style attribute.
export function normalizeAvatarColor(value) {
  if (typeof value !== "string") return "";
  const key = value.trim().toLowerCase();
  return TONE_INDEX.has(key) ? key : "";
}

export function getAvatarToneById(id) {
  const key = normalizeAvatarColor(id);
  return key ? AVATAR_TONES[TONE_INDEX.get(key)] : null;
}

// 32-bit FNV-1a. Small, fast, and identical in every browser, which is all a
// color pick needs.
function hashString(text) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// The identity a color is keyed on: the email when there is one (lowercased,
// so "Sam@X.com" and "sam@x.com" match), otherwise the name.
export function getAvatarKey(name, email) {
  const address = clean(email).toLowerCase();
  if (address) return address;
  return clean(name).toLowerCase().replace(/\s+/g, " ");
}

function hashSlot(key) {
  return hashString(key) % AVATAR_TONES.length;
}

// The tone a person gets with no directory to go on.
export function getHashedAvatarTone(name, email) {
  const key = getAvatarKey(name, email);
  if (!key) return DEFAULT_AVATAR_TONE;
  return AVATAR_TONES[hashSlot(key)];
}

function aliasKeys(member) {
  const raw = Array.isArray(member?.emailAliases) ? member.emailAliases : [];
  return raw.map((alias) => clean(alias).toLowerCase()).filter(Boolean);
}

/**
 * Who in this directory gets which tone.
 *
 * Returns a Map from identity key (see getAvatarKey) to a tone from
 * AVATAR_TONES. A teammate's previous addresses (emailAliases) map to the same
 * tone, so an old address in a log or a picker still shows their color. The
 * first record wins when an address is listed twice.
 */
export function assignAvatarTones(teamMembers = []) {
  const list = Array.isArray(teamMembers) ? teamMembers : [];
  const seen = new Set();
  const people = [];
  for (const member of list) {
    if (!member || typeof member !== "object") continue;
    const key = getAvatarKey(member.name, member.email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    people.push({
      key,
      chosen: normalizeAvatarColor(member.avatarColor),
      aliases: aliasKeys(member),
    });
  }

  const uses = AVATAR_TONES.map(() => 0);
  const toneByKey = new Map();

  // Chosen tones first, so automatic never lands on one somebody picked.
  for (const person of people) {
    if (!person.chosen) continue;
    const index = TONE_INDEX.get(person.chosen);
    uses[index] += 1;
    toneByKey.set(person.key, AVATAR_TONES[index]);
  }

  for (const person of people) {
    if (person.chosen) continue;
    const start = hashSlot(person.key);
    const fewest = Math.min(...uses);
    let index = start;
    for (let step = 0; step < AVATAR_TONES.length; step += 1) {
      const candidate = (start + step) % AVATAR_TONES.length;
      if (uses[candidate] === fewest) {
        index = candidate;
        break;
      }
    }
    uses[index] += 1;
    toneByKey.set(person.key, AVATAR_TONES[index]);
  }

  for (const person of people) {
    const tone = toneByKey.get(person.key);
    for (const alias of person.aliases) {
      if (!toneByKey.has(alias)) toneByKey.set(alias, tone);
    }
  }

  return toneByKey;
}

// The tone for one badge: the explicit choice when the caller passes one, then
// the workspace assignment, then the hash.
export function resolveAvatarTone({ name, email, toneId, assigned } = {}) {
  const explicit = getAvatarToneById(toneId);
  if (explicit) return explicit;
  const key = getAvatarKey(name, email);
  if (!key) return DEFAULT_AVATAR_TONE;
  const fromDirectory =
    assigned && typeof assigned.get === "function" ? assigned.get(key) : null;
  return fromDirectory || AVATAR_TONES[hashSlot(key)];
}
